import React, { useState, useContext, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import Pagination from "../../../../newComponents/utilities/Pagination";
import { DataContext } from "../../../../newComponents/context/DataContext";
import LoadingScreen from "../../../../newComponents/utilities/LoadingScreen";
import Select from "react-select";
import { FaChevronDown, FaChevronUp, FaFilter, FaEye } from "react-icons/fa";

const SponsorDetail = () => {
  const { filteredSponsor, loading, allEventsSummary } = useContext(DataContext);

  // Filter / UI inputs only. The list itself and the filter dropdown options
  // are derived via `useMemo` below so that DataContext's parallel fetches
  // don't trigger setState cascades each time another endpoint resolves —
  // that was the root of the jerky multi-render loading UX.
  const [filters, setFilters] = useState({
    event_name: "",
    states: "",
    cities: "",
    pincodes: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const navigate = useNavigate();

  const sponsors = useMemo(() => {
    if (Array.isArray(filteredSponsor?.sponsors)) return filteredSponsor.sponsors;
    if (Array.isArray(filteredSponsor)) return filteredSponsor;
    return [];
  }, [filteredSponsor]);

  const availableFilters = useMemo(() => {
    if (!sponsors.length) return {};

    const eventSponsorCounts = {};
    const stateSet = new Set();
    const citySet = new Set();
    const pincodeSet = new Set();

    sponsors.forEach((sponsor) => {
      sponsor.event_ids?.forEach((eventId) => {
        if (eventId) {
          eventSponsorCounts[eventId] = (eventSponsorCounts[eventId] || 0) + 1;
        }
      });
      sponsor.states?.forEach((state) => {
        if (state) stateSet.add(state);
      });
      sponsor.cities?.forEach((city) => {
        if (city) citySet.add(city);
      });
      sponsor.pincodes?.forEach((pin) => {
        if (pin) pincodeSet.add(pin);
      });
    });

    const eventOptions = (allEventsSummary || []).map((event) => {
      const eventId = event.event_id;
      const eventName = event.event_name;
      const count = eventId ? eventSponsorCounts[eventId] || 0 : 0;
      return { value: eventName, label: `${eventName} (${count})` };
    });

    return {
      event_name: eventOptions,
      states: [...stateSet].map((option) => ({ value: option, label: option })),
      cities: [...citySet].map((option) => ({ value: option, label: option })),
      pincodes: [...pincodeSet].map((option) => ({ value: option, label: option })),
    };
  }, [sponsors, allEventsSummary]);

  const handleFilterChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    const value = selectedOption ? selectedOption.value : "";
    setFilters({ ...filters, [name]: value });
  };

  const clearAllFilters = () => {
    setFilters({ event_name: "", states: "", cities: "", pincodes: "" });
  };

  const filteredSponsors = useMemo(() => {
    let filtered = sponsors;
    if (filters.event_name) {
      const event = allEventsSummary?.find((e) => e.event_name === filters.event_name);
      const eventId = event?.event_id;
      if (eventId) {
        filtered = filtered.filter((sponsor) => sponsor.event_ids?.includes(eventId));
      }
    }
    if (filters.states) {
      filtered = filtered.filter((sponsor) => sponsor.states?.includes(filters.states));
    }
    if (filters.cities) {
      filtered = filtered.filter((sponsor) => sponsor.cities?.includes(filters.cities));
    }
    if (filters.pincodes) {
      filtered = filtered.filter((sponsor) => sponsor.pincodes?.includes(filters.pincodes));
    }
    return filtered;
  }, [filters, sponsors, allEventsSummary]);

  const totalPages = Math.ceil(filteredSponsors.length / pageSize);
  const paginatedSponsors = filteredSponsors.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleRowClick = (sponsor) => {
    setSelectedParticipant(sponsor);
    navigate(`/sponsors/${sponsor.user_id}/${sponsor.ig_user.id}`, {
      state: {
        sponsor: sponsor,
      },
    });
  };

  const handlePassphraseSubmit = () => {
    if (passphrase.trim() !== "") {
      navigate(
        `/sponsors/${selectedParticipant.user_id}/${selectedParticipant.ig_user.id}`,
        {
          state: {
            sponsor: selectedParticipant,
          },
        }
      );
      setIsModalOpen(false);
      setPassphrase("");
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <main className="lg:mr-8">
      {/* Filters Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6 overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
        >
          <div className="flex items-center gap-3">
            <FaFilter className="text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {Object.values(filters).filter(v => v !== "").length} active
            </span>
          </div>
          <div className="flex items-center gap-2">
            {Object.values(filters).some(v => v !== "") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Clear All
              </button>
            )}
            {isFiltersExpanded ? (
              <FaChevronUp className="text-gray-600" />
            ) : (
              <FaChevronDown className="text-gray-600" />
            )}
          </div>
        </div>
        
        {isFiltersExpanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {Object.keys(filters).map((key) => {
                const placeholder = `Select ${key.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}`;
                const currentValue = availableFilters[key]?.find(option => option.value === filters[key]);
                
                return (
                  <div key={key} className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace("_", " ")}
                    </label>
                    <Select
                      name={key}
                      value={currentValue || null}
                      onChange={handleFilterChange}
                      options={availableFilters[key] || []}
                      placeholder={placeholder}
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          minHeight: '44px',
                          borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
                          boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                          '&:hover': {
                            borderColor: '#9CA3AF',
                          },
                          borderRadius: '8px',
                          fontSize: '14px',
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#EBF4FF' : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          padding: '10px 12px',
                          fontSize: '14px',
                          cursor: 'pointer',
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#374151',
                          fontSize: '14px',
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#9CA3AF',
                          fontSize: '14px',
                        }),
                        menu: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #E5E7EB',
                          zIndex: 9999,
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          padding: '4px',
                          maxHeight: '200px',
                        }),
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sponsors Table */}
      <div className="border border-gray-300 rounded-lg overflow-x-auto my-8">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-100 text-gray-800 text-left">
              <th className="p-4">Profile Pic.</th>
              <th className="p-4">Instagram Username</th>
              <th className="p-4">Followers</th>
              <th className="p-4">Name</th>
              <th className="p-4">Events Sponsored</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedSponsors.map((sponsor, index) => (
              <tr
                key={index}
                onClick={() => handleRowClick(sponsor)}
                className="hover:bg-gray-100 cursor-pointer transition duration-200 border-b ease-in-out"
              >
                <td className="p-4">
                  {(() => {
                    const username = sponsor.ig_user?.username || "";
                    const uid = String(sponsor.user_id || "");
                    const bizId =
                      sponsor.ig_user?.id ||
                      (uid.includes(":") ? uid.split(":")[1] : uid);
                    const igUrl = username
                      ? `https://instagram.com/${username}`
                      : bizId
                      ? `https://www.instagram.com/_u/${bizId}/`
                      : null;
                    const avatar = (
                      <img
                        src={
                          sponsor.ig_user?.profile_picture_url ||
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='8' r='4' fill='%239CA3AF'/%3E%3Cpath fill='%239CA3AF' d='M20 19v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6z'/%3E%3C/svg%3E"
                        }
                        alt="profile-pic"
                        className="w-12 h-12 rounded-full border border-gray-300 shadow-sm hover:scale-105 hover:shadow-lg transition-transform object-cover"
                      />
                    );
                    return igUrl ? (
                      <a
                        href={igUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Open Instagram profile"
                      >
                        {avatar}
                      </a>
                    ) : (
                      avatar
                    );
                  })()}
                </td>
                <td className="p-4">
                  {sponsor.ig_user?.username ? (
                    <a
                      href={`https://instagram.com/${sponsor.ig_user.username}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-blue-600 hover:underline"
                    >
                      @{sponsor.ig_user.username}
                    </a>
                  ) : (() => {
                    // No username known — derive an IG biz id from the user_id
                    // ("facebook:123" or "instagram:123") so admin can still
                    // jump to the profile.
                    const uid = String(sponsor.user_id || "");
                    const bizId = sponsor.ig_user?.id || (uid.includes(":") ? uid.split(":")[1] : uid);
                    if (!bizId) return <span className="text-gray-400">N/A</span>;
                    return (
                      <a
                        href={`https://www.instagram.com/_u/${bizId}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={`Open IG profile (id ${bizId})`}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1"
                      >
                        View profile <span className="text-xs text-gray-400">↗</span>
                      </a>
                    );
                  })()}
                </td>
                <td className="p-4">{(sponsor.ig_user?.followers_count ?? 0).toLocaleString()}</td>
                <td className="p-4">
                  {sponsor.ig_user?.name || <span className="text-gray-400">—</span>}
                </td>
                <td className="p-4">{sponsor.eventsSponsored || 0}</td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <Link
                    to={`/sponsor/view/${sponsor.user_id}`}
                    state={{ sponsor }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm"
                  >
                    <FaEye size={12} /> View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* Modal for Passphrase */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded p-6 w-full max-w-sm">
            <h3 className="text-lg mb-4">Enter Passphrase</h3>
            <input
              type="text"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder="Passphrase"
              className="border w-full px-3 py-2 rounded mb-4"
            />
            <div className="flex justify-between">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
              >
                Cancel
              </button>
              <button
                onClick={handlePassphraseSubmit}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default SponsorDetail;
